<template>
	<v-chart
		class="chart"
		:option="option"
		ref="chart"
		:init-options="{ renderer: 'svg' }"
		autoresize
	/>
</template>

<script>
// https://github.com/ecomfe/vue-echarts?tab=readme-ov-file
import VChart from 'vue-echarts';
import { use } from 'echarts/core'
import { GaugeChart } from 'echarts/charts'
import { TitleComponent } from 'echarts/components'
import { CanvasRenderer, SVGRenderer, } from 'echarts/renderers'

use([
	TitleComponent,
	GaugeChart,
	CanvasRenderer,
	SVGRenderer,
])

export const red = '#f2495c'
export const orange = '#ff9830'
export const blue = '#5794f2'
export const chartMinValue = 0 // 0 .. 10
export const chartMaxValue = -4 // -4 .. 0
export const chartRange = Math.abs(chartMaxValue) + Math.abs(chartMinValue)
export function makeAxisLineColorRages() {
	//const COLOR_BORDERS = [3.5, 2.5, 0.85, 0]
	const startRedZoneLen = chartMinValue + 0.85
	const blueZoneLen = 2.5 - 0.85
	const orangeZoneLen = 3.5 - 2.5
	const endRedZoneLen = Math.abs(chartMaxValue) - 3.5
	
	// Check for errors
	if ((startRedZoneLen + blueZoneLen + orangeZoneLen + endRedZoneLen) != chartRange) {
		console.error('Sum of U protect chart segments is not equal chart range')
	}
	//console.log('Chart ring len = ', startRedZoneLen + blueZoneLen + orangeZoneLen + endRedZoneLen)
	
	return [
		[startRedZoneLen / chartRange, red],
		[(startRedZoneLen + blueZoneLen) / chartRange, blue],
		[(startRedZoneLen + blueZoneLen + orangeZoneLen) / chartRange, orange],
		[(startRedZoneLen + blueZoneLen + orangeZoneLen + endRedZoneLen) / chartRange, red],
	]
}

export default {
	RED: red,
	ORANGE: orange,
	BLUE: blue,
	GRAY: '#ccccdc',
	TITLE_COLOR: '#2c3e50',
	// Center position Default 50%, 50% 
	CENTER_HORIZONTAL: '50%',
	CENTER_VERTICAL: '75%',
	// Round start, end
	START_ANGLE: 200,
	END_ANGLE: -20,
	//
	CHART_MIN_VALUE: chartMinValue,
	CHART_MAX_VALUE: chartMaxValue,
	CHART_RANGE: chartRange,
	MAX_CHART_SPLIT_SEGMENTS: 10, // max segment per chart
	PROGRESS_LINE_WIDTH: 15,
	// The axis color ranges. Sequence: red, orange, blue, red.
	//COLOR_BORDERS: [3.5, 2.5, 0.85, 0],

	props: {
		value: {
			type: [Number, String],
			require: false,
			default: undefined,
		},
	},

	components: {
		VChart,
	},

	setup() {
		const unit = 'В'
		return {
			unit,
		}
	},

	data() {
		return {
			// Graph configuration
			option: {
				title: {
					text: 'Захисний потенціал',
					left: 'center',
					top: '3%',
					textStyle: {
						color: this.$options.TITLE_COLOR,
						fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
						fontStyle: 'normal',
						fontSize: 14.28,
						fontWeight: 700,
					},
				},
				series: [
					// Value ring
					{
						type: 'gauge',
						zlevel: 1,
						startAngle: this.$options.START_ANGLE,
						endAngle: this.$options.END_ANGLE,
						center: [
							this.$options.CENTER_HORIZONTAL,
							this.$options.CENTER_VERTICAL
						],
						min: this.$options.CHART_MIN_VALUE,
						max: this.$options.CHART_MAX_VALUE,
						// The number of split segments of gauge chart scale
						splitNumber: this.$options.CHART_RANGE <= this.$options.MAX_CHART_SPLIT_SEGMENTS
							? this.$options.CHART_RANGE
							: this.$options.MAX_CHART_SPLIT_SEGMENTS,
						itemStyle: {
							color: this.$options.GRAY
						},
						progress: {
							show: true,
							overlap: true,
							width: this.$options.PROGRESS_LINE_WIDTH,
						},
						pointer: {
							show: false, // Remove pointer
						},

						// Line behind the chart round
						axisLine: {
							lineStyle: {
								width: -2,
								color: makeAxisLineColorRages(),
								// shadowColor: 'rgba(0, 0, 0, 0.5)',
								// shadowBlur: 20
							},
						},
						axisTick: {
							show: false, // Remove tics
							distance: -8,
							splitNumber: 5,
							lineStyle: {
								width: 1,
								color: this.$options.GRAY
							}
						},
						splitLine: {
							show: true,
							distance: -4,
							length: 4,
							lineStyle: {
								width: 2,
								color: '#999'
							}
						},
						axisLabel: {
							distance: -22,
							color: this.$options.TITLE_COLOR,
							fontSize: 12,
						},

						anchor: {
							show: false
						},
						title: {
							show: false
						},
						detail: {
							valueAnimation: true,
							width: '60%',
							lineHeight: 10,
							borderRadius: 8,
							offsetCenter: [0, '20%'],
							fontSize: 18,
							fontWeight: 'bolder',
							formatter: () => this.value == undefined ? '-' : `${this.value} ${this.unit}`,
							color: 'inherit'
						},
						data: [
							{
								value: 0
							}
						]
					},
					// Gray ring
					{
						type: 'gauge',
						zlevel: 0,
						startAngle: this.$options.START_ANGLE,
						endAngle: this.$options.END_ANGLE,
						center: [
							this.$options.CENTER_HORIZONTAL,
							this.$options.CENTER_VERTICAL
						],
						min: this.$options.CHART_MIN_VALUE,
						max: this.$options.CHART_MAX_VALUE,
						splitNumber: 0, // The number of split segments of gauge chart scale
						itemStyle: {
							color: '#f4f5f5' // gray
						},
						progress: {
							show: true,
							overlap: true,
							width: this.$options.PROGRESS_LINE_WIDTH,
						},
						pointer: {
							show: false
						},
						axisLine: {
							show: false
						},
						axisTick: {
							show: false
						},
						splitLine: {
							show: false
						},
						axisLabel: {
							show: false
						},
						detail: {
							show: false
						},
						data: [
							{
								value: this.$options.CHART_MAX_VALUE,
							}
						]
					},

					// White spaser ring
					{
						type: 'gauge',
						zlevel: 2,
						startAngle: this.$options.START_ANGLE,
						endAngle: this.$options.END_ANGLE,
						center: [
							this.$options.CENTER_HORIZONTAL,
							this.$options.CENTER_VERTICAL
						],
						min: this.$options.CHART_MIN_VALUE,
						max: this.$options.CHART_MAX_VALUE,
						splitNumber: 0, // The number of split segments of gauge chart scale
						itemStyle: {
							color: '#ffffff' // white
						},
						progress: {
							show: true,
							overlap: true,
							width: 2
						},
						pointer: {
							show: false
						},
						axisLine: {
							show: false
						},
						axisTick: {
							show: false
						},
						splitLine: {
							show: false
						},
						axisLabel: {
							show: false
						},
						detail: {
							show: false
						},
						data: [
							{
								value: this.$options.CHART_MAX_VALUE,
							}
						]
					},
				]
			},
		};
	},

	watch: {
		// Update chart on value update
		value(/*newCriterias*/) {
			//console.log('update value')
			const series = this.option.series[0]
			series.data[0].value = this.chartValue
			series.itemStyle.color = this.valueColor(this.value) // dynamic color
		}
	},

	created() {},

	mounted() {
		//
		const series = this.option.series[0]
		series.data[0].value = this.chartValue	// set value
		series.itemStyle.color = this.valueColor(this.value) // dynamic color

		// setInterval(() => {
		// 	this.value = -(Math.random() * this.$options.CHART_RANGE)
		// }, 2000);
	},
	
	methods: {
		valueColor(value) {
			if (value <= -3.5) {
				return this.$options.RED
			} else if (value > -3.5 && value <= -2.5) {
				return this.$options.ORANGE
			} else if (value > -2.5 && value <= -0.85) {
				return this.$options.BLUE
			} else if (value > -0.85 && value <= 0) {
				return this.$options.RED
			} else {
				return this.$options.GRAY
			}
		},
	},

	computed: {
		chartRange() {
			return this.$options.CHART_MAX_VALUE - this.$options.CHART_MIN_VALUE
		},

		chartValue() {
			return this.value ? Number(this.value).toFixed(2) : this.value
		},

		chartInstance() {
			return this.$refs.chart?.chart
		},
	},
}
</script>


<style scoped>
.chart {
  /* height: 22vh; */
	height: 100%;
}
</style>